import { Input } from '@shopflo/ui'
import { clsx } from 'clsx'
import React, { useMemo, useRef, useState } from 'react'

type AllowedInputTypes = 'password' | 'text' | 'number' | 'tel'

interface OTPInputProps {
  OTPLength: number
  disabled: boolean
  autoFocus: boolean
  hasError: boolean
  value: string
  onChange: (otp: string) => void
  onSubmit?: (otp: string) => void
  inputType?: AllowedInputTypes
  containerCustomClass?: object
  inputClassName?: string
  disableTheme?: boolean
  isLoading?: boolean
}

const OTPInput: React.FC<OTPInputProps> = ({
  value = '',
  OTPLength = 4,
  onChange,
  onSubmit,
  autoFocus = false,
  inputType = 'tel',
  inputClassName,
  disabled,
  isLoading = false
}) => {
  const [activeInput, setActiveInput] = useState(0)
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const isDisabled = disabled || isLoading
  const isInputNum = inputType === 'number' || inputType === 'tel'
  const otpValueArray = value.toString().split('')

  const focusInput = (index: number) => {
    if (isDisabled) return

    const targetIndex = Math.max(Math.min(OTPLength - 1, index), 0)
    const targetInput = inputRefs.current[targetIndex]

    if (targetInput) {
      targetInput.focus()
      const value = otpValueArray[targetIndex]
      if (value) {
        targetInput.select()
      }
      setActiveInput(targetIndex)
    }
  }

  const changeCodeAtFocus = (newValue: string) => {
    if (isDisabled) return

    const newOtpArray = [...otpValueArray]
    newOtpArray[activeInput] = newValue[0]
    const newOtp = newOtpArray.join('')
    onChange(newOtp)

    if (newOtp.length === OTPLength) {
      onSubmit?.(newOtp)
    }
  }

  const handleOTPChange = (otp: Array<string>) => {
    if (isDisabled) return
    const newOtp = otp.join('')
    onChange(newOtp)

    if (newOtp.length === OTPLength) {
      onSubmit?.(newOtp)
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return

    const inputValue = event.target.value

    if (inputValue.length && Number.isInteger(Number(inputValue))) {
      changeCodeAtFocus(inputValue)
      focusInput(activeInput + 1)
    } else {
      const { nativeEvent } = event

      const isBackspace =
        nativeEvent instanceof InputEvent &&
        nativeEvent.data === null &&
        nativeEvent.inputType === 'deleteContentBackward'

      if (isBackspace) {
        event.preventDefault()
        changeCodeAtFocus('')
        focusInput(activeInput - 1)
      }
    }
  }

  const handleFocus = (index: number) => (event: React.FocusEvent<HTMLInputElement>) => {
    if (isDisabled) return

    setActiveInput(index)
    const value = otpValueArray[index]
    if (value) {
      event.target.select()
    }
  }

  const handleBlur = () => {
    if (isDisabled) return
    setActiveInput(prev => Math.max(0, prev - 1))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) return

    const currentValue = otpValueArray[activeInput]
    const isEmpty = !currentValue

    switch (event.key) {
      case 'Backspace':
        event.preventDefault()
        if (isEmpty) {
          const prevIndex = Math.max(0, activeInput - 1)
          const newOtpArray = [...otpValueArray]
          newOtpArray[prevIndex] = ''
          handleOTPChange(newOtpArray)
          focusInput(prevIndex)
        } else {
          changeCodeAtFocus('')
        }
        break

      case 'Delete':
        event.preventDefault()
        changeCodeAtFocus('')
        break

      case 'ArrowLeft':
        event.preventDefault()
        focusInput(activeInput - 1)
        break

      case 'ArrowRight':
        event.preventDefault()
        focusInput(activeInput + 1)
        break

      case currentValue:
        event.preventDefault()
        focusInput(activeInput + 1)
        break

      case 'Spacebar':
      case 'Space':
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault()
        break

      default:
        if (Number.isInteger(Number(event.key))) {
          event.preventDefault()
          changeCodeAtFocus(event.key)
          focusInput(activeInput + 1)
        }
        break
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (isDisabled) return

    event.preventDefault()

    const pastedData = event.clipboardData
      .getData('text/plain')
      .slice(0, OTPLength - activeInput)
      .split('')

    if (isInputNum && pastedData.some(value => isNaN(Number(value)))) {
      return
    }

    const newOtpArray = [...otpValueArray]
    const availableSlots = OTPLength - activeInput
    const dataToPaste = pastedData.slice(0, availableSlots)

    for (let i = 0; i < dataToPaste.length; i++) {
      newOtpArray[activeInput + i] = dataToPaste[i]
    }

    const nextActiveInput = Math.min(activeInput + dataToPaste.length, OTPLength)

    handleOTPChange(newOtpArray)
    focusInput(nextActiveInput)
  }

  return (
    <div
      className={clsx('flex w-full items-center justify-center gap-2 relative', isLoading && 'pointer-events-none')}
      data-testid='otp-input-root'
    >
      {Array.from({ length: OTPLength }).map((_, index) => {
        const value = otpValueArray[index]
        const isActive = activeInput === index
        const isEmpty = !value

        return (
          <Input
            key={index}
            className={clsx(
              'w-11 h-11 text-center text-xl',
              isLoading && 'opacity-50 cursor-not-allowed animate-pulse',
              inputClassName,
              isEmpty && 'text-zinc-400'
            )}
            style={{
              caretColor: isEmpty ? 'transparent' : 'auto',
              animationDuration: isLoading ? '1.5s' : 'auto'
            }}
            variant='filled'
            dimension='lg'
            id={`flo__auth__otpInputField${index + 1}`}
            value={isActive && isEmpty ? '-' : value ?? ''}
            ref={element => {
              inputRefs.current[index] = element
            }}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus(index)}
            onBlur={handleBlur}
            type='tel'
            onPaste={handlePaste}
            disabled={isDisabled}
            autoFocus={autoFocus && index === 0 && !isDisabled}
            data-testid='input'
            autoComplete='off'
          />
        )
      })}
    </div>
  )
}

export default OTPInput
